import React, { useState } from 'react';
import { toast } from 'sonner';
import { ArrowRight, Check, Loader2, Mail } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable newsletter signup card used on Landing footer, Health Score result, blog posts.
 *
 * Props:
 *   variant: 'dark' | 'light'      visual style
 *   source:  string                analytics tag passed to backend (e.g. "landing_footer")
 *   compact: boolean               single-row inline form vs full card
 *   eyebrow, title, subtitle:      text overrides
 */
const NewsletterSignup = ({
  variant = 'dark',
  source = 'landing',
  compact = false,
  eyebrow = 'THE FINOPS BRIEF',
  title = '3 cost-saving tips, every Tuesday.',
  subtitle = 'Built for finance & engineering teams who are tired of paying for cloud they don\'t use. No fluff. Just what works.',
}) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error

  const isDark = variant === 'dark';

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email');
      return;
    }
    setStatus('loading');
    try {
      const { data } = await axios.post(`${API}/api/newsletter/subscribe`, { email, source });
      setStatus('success');
      toast.success(data.already_subscribed
        ? 'You\'re already on the list — thanks for the love.'
        : 'You\'re in. Watch your inbox 🌿');
    } catch (err) {
      setStatus('error');
      toast.error('Couldn\'t subscribe right now. Try again in a moment.');
    }
  };

  if (status === 'success') {
    return (
      <div
        className={`rounded-xl p-6 text-center ${
          isDark
            ? 'bg-emerald-500/10 border border-emerald-500/30'
            : 'bg-emerald-50 border border-emerald-200'
        }`}
        data-testid="newsletter-success"
      >
        <div className="inline-flex w-12 h-12 rounded-full bg-emerald-500/20 items-center justify-center mb-3">
          <Check className="w-6 h-6 text-emerald-500" />
        </div>
        <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>You're in.</p>
        <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-400'}`}>
          First issue lands next Tuesday.
        </p>
      </div>
    );
  }

  if (compact) {
    return (
      <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-2 w-full max-w-md" data-testid="newsletter-form-compact">
        <div className="relative flex-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            disabled={status === 'loading'}
            className={`w-full pl-9 pr-3 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 ${
              isDark
                ? 'bg-white/5 border border-white/10 text-white placeholder:text-slate-400'
                : 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400'
            }`}
            data-testid="newsletter-email-input"
          />
        </div>
        <button
          type="submit"
          disabled={status === 'loading'}
          className="inline-flex items-center justify-center gap-1 px-4 py-2.5 rounded-lg text-sm font-semibold bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60 transition-colors"
          data-testid="newsletter-submit-btn"
        >
          {status === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Subscribe'}
          {status !== 'loading' && <ArrowRight className="w-4 h-4" />}
        </button>
      </form>
    );
  }

  return (
    <div
      className={`rounded-2xl p-6 sm:p-8 ${
        isDark
          ? 'bg-slate-900 bg-gradient-to-br from-emerald-500/10 via-slate-900 to-slate-950 border border-emerald-500/20'
          : 'bg-emerald-50 bg-gradient-to-br from-emerald-50 via-white to-white border border-emerald-100'
      }`}
      data-testid="newsletter-card"
    >
      <p className={`text-[11px] font-bold tracking-[0.2em] mb-3 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
        {eyebrow}
      </p>
      <h3 className={`text-xl sm:text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
        {title}
      </h3>
      <p className={`text-sm mb-5 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{subtitle}</p>
      <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-2" data-testid="newsletter-form">
        <div className="relative flex-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            disabled={status === 'loading'}
            className={`w-full pl-9 pr-3 py-3 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 ${
              isDark
                ? 'bg-white/5 border border-white/10 text-white placeholder:text-slate-400'
                : 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400'
            }`}
          />
        </div>
        <button
          type="submit"
          disabled={status === 'loading'}
          className="inline-flex items-center justify-center gap-1 px-5 py-3 rounded-lg text-sm font-semibold bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60 transition-colors"
        >
          {status === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Subscribe'}
          {status !== 'loading' && <ArrowRight className="w-4 h-4" />}
        </button>
      </form>
      <p className={`text-xs mt-3 ${isDark ? 'text-slate-400' : 'text-slate-400'}`}>
        Unsubscribe anytime. We never sell your data.
      </p>
    </div>
  );
};

export default NewsletterSignup;
